<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useTemplateVariableMapping } from 'dashboard/composables/useTemplateVariableMapping';

import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
  getMediaHeaderSampleUrlFromTemplate,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  contact: {
    type: Object,
    default: null,
  },
  enableVariableMapping: {
    type: Boolean,
    default: false,
  },
  /** When true, media header URL/handle is not shown (e.g. campaigns use synced sample / backend merge). */
  hideMediaHeaderInput: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();

const processedParams = ref({});
const templateVariableMappings = ref({});
const variableSelections = ref({});

const contactAttributes = useMapGetter('attributes/getContactAttributes');
const contactRef = computed(() => props.contact);

const {
  variableMappingOptions,
  isStaticMode: _isStaticMode,
  handleVariableSelect: _handleVariableSelect,
  getOptionLabel,
  resolveContactValue,
} = useTemplateVariableMapping({
  i18nBaseKey: 'WHATSAPP_TEMPLATES.PARSER.VARIABLE_OPTIONS',
  variablePattern: /\{\{([^}]+)\}\}/g,
  contact: contactRef,
  contactAttributes,
});

const useVariableMapping = computed(
  () => props.enableVariableMapping || !!props.contact
);

const isStaticMode = varKey => _isStaticMode(variableSelections.value, varKey);

const handleVariableSelect = (varKey, val) =>
  _handleVariableSelect(
    variableSelections,
    templateVariableMappings,
    varKey,
    val
  );

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const hasMediaHeader = computed(() => {
  const fmt = String(headerComponent.value?.format ?? '').toUpperCase();
  return MEDIA_FORMATS.includes(fmt);
});

const hasGcsMedia = computed(() => !!props.template.gcs_media_url);

const formatType = computed(() => {
  const fmt = String(headerComponent.value?.format ?? '').toUpperCase();
  if (!fmt) return '';
  return fmt.charAt(0) + fmt.slice(1).toLowerCase();
});

const hasVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

const bodyVariableKeys = computed(() => {
  if (!processedParams.value.body) return [];
  return Object.keys(processedParams.value.body);
});

const renderedTemplate = computed(() => {
  if (useVariableMapping.value && hasVariables.value) {
    let rendered = bodyText.value;
    const selections = variableSelections.value || {};

    if (Object.keys(selections).length > 0) {
      rendered = rendered.replace(/{{([^}]+)}}/g, (match, variable) => {
        const cleanVariable = variable.trim();
        const selection = selections[cleanVariable];
        if (!selection) return match;

        if (selection === '__static__') {
          return templateVariableMappings.value[cleanVariable] || match;
        }

        const resolved = resolveContactValue(selection, props.contact);
        if (resolved) return resolved;

        return `[${getOptionLabel(selection)}]`;
      });
    }
    return rendered;
  }
  return replaceTemplateVariables(bodyText.value, processedParams.value);
});

const isFormInvalid = computed(() => {
  if (!hasVariables.value && !hasMediaHeader.value) return false;

  if (
    hasMediaHeader.value &&
    !hasGcsMedia.value &&
    !props.hideMediaHeaderInput &&
    !processedParams.value.header?.media_url

  ) {
    return true;
  }

  if (useVariableMapping.value && hasVariables.value) {
    const hasEmptyVariable = bodyVariableKeys.value.some(varKey => {
      const selection = variableSelections.value[varKey];
      if (!selection) return true;
      if (selection === '__static__') {
        return !templateVariableMappings.value[varKey];
      }
      return false;
    });
    if (hasEmptyVariable) return true;
  } else if (hasVariables.value && processedParams.value.body) {
    const hasEmptyBodyVariable = Object.values(processedParams.value.body).some(
      value => !value
    );
    if (hasEmptyBodyVariable) return true;
  }

  if (processedParams.value.buttons) {
    const hasEmptyButtonParameter = processedParams.value.buttons.some(
      button => !button.parameter
    );
    if (hasEmptyButtonParameter) return true;
  }

  return false;
});

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const initializeTemplateParameters = () => {
  const base = buildTemplateParameters(props.template, hasMediaHeader.value);
  const sampleUrl = getMediaHeaderSampleUrlFromTemplate(props.template);
  if (hasMediaHeader.value && sampleUrl && base.header) {
    base.header.media_url = sampleUrl;
  }
  processedParams.value = base;
  templateVariableMappings.value = {};
  variableSelections.value = {};
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

// Computed that returns processedParams with variable mapping values resolved
const resolvedProcessedParams = computed(() => {
  if (!useVariableMapping.value || !hasVariables.value) {
    return processedParams.value;
  }

  const resolved = { ...processedParams.value };
  if (resolved.body) {
    resolved.body = { ...resolved.body };
    bodyVariableKeys.value.forEach(varKey => {
      const selection = variableSelections.value[varKey];
      if (selection === '__static__') {
        resolved.body[varKey] = templateVariableMappings.value[varKey] || '';
      } else if (selection) {
        const contactResolved = resolveContactValue(selection, props.contact);
        resolved.body[varKey] = contactResolved || selection;
      }
    });
  }
  return resolved;
});

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid || isFormInvalid.value) return;

  const { name, category, language, namespace } = props.template;

  const payload = {
    message: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params: resolvedProcessedParams.value,
    },
  };
  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(initializeTemplateParameters);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

defineExpose({
  processedParams: resolvedProcessedParams,
  hasVariables,
  hasMediaHeader,
  headerComponent,
  renderedTemplate,
  v$,
  updateMediaUrl,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-md bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <div v-if="hasVariables || hasMediaHeader">

      <div
        v-if="hasMediaHeader && !hasGcsMedia && !props.hideMediaHeaderInput"
        class="mb-4"
      >
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>
        <div class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_url || ''"
            type="url"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: formatType,
              })
            "
            @update:model-value="updateMediaUrl"
          />
        </div>
      </div>

      <!-- Body Variables Section with variable mapping -->
      <div v-if="processedParams.body && useVariableMapping">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="varKey in bodyVariableKeys"
          :key="`body-mapping-${varKey}`"
          class="mb-2.5"
        >
          <div class="flex items-center gap-3">
            <span
              class="bg-n-alpha-black2 text-n-slate-12 inline-block rounded-md text-xs py-2.5 px-6"
            >
              {{ varKey }}
            </span>
            <ComboBox
              :model-value="variableSelections[varKey] || ''"
              :options="variableMappingOptions"
              :placeholder="
                t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_SELECT_PLACEHOLDER')
              "
              appearance="input"
              class="flex-1"
              @update:model-value="val => handleVariableSelect(varKey, val)"
            />
          </div>
          <div v-if="isStaticMode(varKey)" class="flex items-center gap-3 mt-2">
            <span class="invisible inline-block rounded-md text-xs py-2.5 px-6">
              {{ varKey }}
            </span>
            <Input
              v-model="templateVariableMappings[varKey]"
              type="text"
              class="flex-1"
              :placeholder="
                t('WHATSAPP_TEMPLATES.PARSER.STATIC_VALUE_PLACEHOLDER')
              "
            />
          </div>
        </div>
        <p class="text-xs text-n-slate-11 mt-1">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_HINT') }}
        </p>
      </div>

      <!-- Body Variables Section with plain inputs (fallback when no contact) -->
      <div v-else-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
